import React, { useEffect, useRef } from 'react';
import './TradingWidgets.css';

// TradingView Ticker Tape Widget
export function TickerTape() {
  const containerRef = useRef(null);
  const scriptAddedRef = useRef(false);

  useEffect(() => {
    if (scriptAddedRef.current || !containerRef.current) return;
    scriptAddedRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbols: [
        { proName: "BINANCE:BNBUSDT", title: "BNB/USDT" },
        { proName: "BINANCE:BTCUSDT", title: "BTC/USDT" },
        { proName: "BINANCE:ETHUSDT", title: "ETH/USDT" },
        { proName: "BINANCE:SOLUSDT", title: "SOL/USDT" }
      ],
      showSymbolLogo: true,
      colorTheme: "dark",
      isTransparent: true,
      displayMode: "adaptive",
      locale: "en"
    });

    containerRef.current.appendChild(script);
  }, []);

  return (
    <div className="ticker-tape-container">
      <div className="tradingview-widget-container" ref={containerRef}>
        <div className="tradingview-widget-container__widget"></div>
      </div>
    </div>
  );
}

// TradingView Mini Chart Widget
export function MiniChart({ symbol = "BINANCE:BNBUSDT" }) {
  const containerRef = useRef(null);
  const scriptAddedRef = useRef(false);

  useEffect(() => {
    if (scriptAddedRef.current || !containerRef.current) return;
    scriptAddedRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol: symbol,
      width: "100%",
      height: "100%",
      locale: "en",
      dateRange: "1D",
      colorTheme: "dark",
      isTransparent: true,
      autosize: true,
      largeChartUrl: ""
    });

    containerRef.current.appendChild(script);
  }, [symbol]);

  return (
    <div className="mini-chart-container">
      <div className="tradingview-widget-container" ref={containerRef}>
        <div className="tradingview-widget-container__widget"></div>
      </div>
    </div>
  );
}

// AI Indicator Widget
export function AIIndicator({ value = 78 }) {
  return (
    <div className="ai-indicator-widget">
      <div className="ai-rings">
        <div className="ai-ring ring-1"></div>
        <div className="ai-ring ring-2"></div>
        <div className="ai-ring ring-3"></div>
        <div className="ai-core">
          <span className="ai-value">{value}</span>
          <span className="ai-label">AI Score</span>
        </div>
      </div>
      <div className="ai-status">
        <span className="status-dot active"></span>
        <span>AI Trading Active</span>
      </div>
    </div>
  );
}

// Live Prices Widget
export function LivePrices({ prices }) {
  const defaultPrices = [
    { symbol: 'BNB', price: 312.45, change: 2.34 },
    { symbol: 'BTC', price: 43250.00, change: 1.25 },
    { symbol: 'ETH', price: 2280.50, change: -0.85 }
  ];

  const data = prices || defaultPrices;

  return (
    <div className="live-prices-widget">
      <div className="widget-header">
        <span className="widget-title">Live Prices</span>
        <span className="live-dot"></span>
      </div>
      <div className="prices-list">
        {data.map((item, index) => (
          <div key={index} className="price-item">
            <span className="price-symbol">{item.symbol}</span>
            <span className="price-value">${item.price.toLocaleString()}</span>
            <span className={`price-change ${item.change >= 0 ? 'positive' : 'negative'}`}>
              {item.change >= 0 ? '+' : ''}{item.change}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Animated Counter
export function AnimatedCounter({ value, prefix = '$', suffix = '', decimals = 2 }) {
  const [displayValue, setDisplayValue] = React.useState(0);

  useEffect(() => {
    const target = parseFloat(value) || 0;
    const duration = 1000;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        current = target;
        clearInterval(timer);
      }
      setDisplayValue(current);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className="animated-counter">
      {prefix}{displayValue.toFixed(decimals)}{suffix}
    </span>
  );
}

// Trade Timer Component
export function TradeTimer({ startTime, roiTimeframe = 86400 }) {
  const [timeLeft, setTimeLeft] = React.useState(0);
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    const calculateTime = () => {
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - startTime;
      const remaining = Math.max(0, roiTimeframe - elapsed);
      const progressPct = Math.min((elapsed / roiTimeframe) * 100, 100);
      
      setTimeLeft(remaining);
      setProgress(progressPct);
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [startTime, roiTimeframe]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isComplete = timeLeft === 0;

  return (
    <div className={`trade-timer ${isComplete ? 'complete' : ''}`}>
      <div className="timer-ring">
        <svg viewBox="0 0 100 100">
          <circle className="timer-bg" cx="50" cy="50" r="45" />
          <circle 
            className="timer-progress" 
            cx="50" cy="50" r="45"
            style={{ strokeDashoffset: 283 - (283 * progress / 100) }}
          />
        </svg>
        <div className="timer-content">
          <span className="timer-icon">{isComplete ? '✓' : '⏱️'}</span>
          <span className="timer-value">{isComplete ? 'Ready!' : formatTime(timeLeft)}</span>
          <span className="timer-label">{isComplete ? 'Claim Now' : 'Next Profit'}</span>
        </div>
      </div>
    </div>
  );
}

// Data Stream Animation
export function DataStream() {
  return (
    <div className="data-stream">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="stream-line" style={{ animationDelay: `${i * 0.5}s` }}>
          {[...Array(20)].map((_, j) => (
            <span key={j} className="stream-char">{Math.random() > 0.5 ? '1' : '0'}</span>
          ))}
        </div>
      ))}
    </div>
  );
}
